import { useQuery } from '@tanstack/react-query';
import {
  Baby,
  Heart,
  Calendar,
  ListChecks,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';

const supabase = getSupabaseAdminClient();

const FEATURE_COLORS = {
  swipes: '#f43f5e',
  checklists: '#8b5cf6',
  appointments: '#3b82f6',
  ai: '#10b981',
  partners: '#f59e0b',
};

export function FeatureAnalyticsPage() {
  // Fetch feature usage stats
  const { data: featureStats, isLoading: loadingFeatures } = useQuery({
    queryKey: ['feature-usage-stats'],
    queryFn: async () => {
      const [swipesResult, checklistsResult, appointmentsResult, aiResult, partnersResult] = await Promise.all([
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }),
        supabase.from('user_checklists').select('id', { count: 'exact', head: true }),
        supabase.from('user_appointments').select('id', { count: 'exact', head: true }),
        supabase.from('ai_usage').select('questions_count'),
        supabase.from('partner_connections').select('id', { count: 'exact', head: true }).eq('status', 'active'),
      ]);

      const totalAIQuestions = (aiResult.data as { questions_count: number }[] | null)?.reduce(
        (sum, d) => sum + d.questions_count,
        0
      ) || 0;

      return {
        swipes: swipesResult.count || 0,
        checklists: checklistsResult.count || 0,
        appointments: appointmentsResult.count || 0,
        ai: totalAIQuestions,
        partners: partnersResult.count || 0,
      };
    },
  });

  // Fetch swipe analytics
  const { data: swipeAnalytics, isLoading: loadingSwipes } = useQuery({
    queryKey: ['swipe-analytics'],
    queryFn: async () => {
      const [totalSwipes, likes, dislikes, favorites] = await Promise.all([
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }),
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }).eq('swipe_type', 'like'),
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }).eq('swipe_type', 'dislike'),
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }).eq('swipe_type', 'favorite'),
      ]);

      return {
        total: totalSwipes.count || 0,
        likes: likes.count || 0,
        dislikes: dislikes.count || 0,
        favorites: favorites.count || 0,
        likeRate: totalSwipes.count ? ((likes.count || 0) / totalSwipes.count * 100).toFixed(1) : 0,
      };
    },
  });

  // Fetch appointment types distribution
  const { data: appointmentTypes, isLoading: loadingAppointments } = useQuery({
    queryKey: ['appointment-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_appointments')
        .select('appointment_type');

      if (error) throw error;

      const typeMap: Record<string, number> = {};
      (data as { appointment_type: string }[])?.forEach(a => {
        typeMap[a.appointment_type] = (typeMap[a.appointment_type] || 0) + 1;
      });

      const typeLabels: Record<string, string> = {
        arzttermin: 'Arzttermin',
        vorsorge: 'Vorsorge',
        ultraschall: 'Ultraschall',
        kurs: 'Kurs',
        personal: 'Persönlich',
      };

      const typeColors: Record<string, string> = {
        arzttermin: '#ef4444',
        vorsorge: '#3b82f6',
        ultraschall: '#8b5cf6',
        kurs: '#10b981',
        personal: '#f59e0b',
      };

      return Object.entries(typeMap).map(([type, count]) => ({
        type,
        label: typeLabels[type] || type,
        count,
        color: typeColors[type] || '#6b7280',
      })).sort((a, b) => b.count - a.count);
    },
  });

  // Fetch checklist completion rates
  const { data: checklistStats, isLoading: loadingChecklists } = useQuery({
    queryKey: ['checklist-completion'],
    queryFn: async () => {
      const { data: items, error } = await supabase
        .from('user_checklist_items')
        .select('status');

      if (error) throw error;

      const total = items?.length || 0;
      const completed = (items as { status: string }[])?.filter(i => i.status === 'completed').length || 0;
      const pending = total - completed;

      return {
        total,
        completed,
        pending,
        completionRate: total ? ((completed / total) * 100).toFixed(1) : 0,
      };
    },
  });

  // Fetch weekly feature usage trends
  const { data: weeklyTrends, isLoading: loadingTrends } = useQuery({
    queryKey: ['weekly-feature-trends'],
    queryFn: async () => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const [swipesData, appointmentsData, checklistsData] = await Promise.all([
        supabase
          .from('name_swipes')
          .select('created_at')
          .gte('created_at', sevenDaysAgo.toISOString()),
        supabase
          .from('user_appointments')
          .select('created_at')
          .gte('created_at', sevenDaysAgo.toISOString()),
        supabase
          .from('user_checklists')
          .select('created_at')
          .gte('created_at', sevenDaysAgo.toISOString()),
      ]);

      const dayMap: Record<string, { swipes: number; appointments: number; checklists: number }> = {};

      // Initialize all days
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const day = date.toLocaleDateString('de-DE', { weekday: 'short' });
        dayMap[day] = { swipes: 0, appointments: 0, checklists: 0 };
      }

      // Count swipes
      (swipesData.data as { created_at: string }[] | null)?.forEach(s => {
        const day = new Date(s.created_at).toLocaleDateString('de-DE', { weekday: 'short' });
        if (dayMap[day]) dayMap[day].swipes++;
      });

      // Count appointments
      (appointmentsData.data as { created_at: string }[] | null)?.forEach(a => {
        const day = new Date(a.created_at).toLocaleDateString('de-DE', { weekday: 'short' });
        if (dayMap[day]) dayMap[day].appointments++;
      });

      // Count checklists
      (checklistsData.data as { created_at: string }[] | null)?.forEach(c => {
        const day = new Date(c.created_at).toLocaleDateString('de-DE', { weekday: 'short' });
        if (dayMap[day]) dayMap[day].checklists++;
      });

      return Object.entries(dayMap).map(([day, data]) => ({
        day,
        ...data,
      }));
    },
  });

  const isLoading = loadingFeatures || loadingSwipes || loadingAppointments || loadingChecklists || loadingTrends;

  const pieData = featureStats ? [
    { name: 'Namen-Swipes', value: featureStats.swipes, color: FEATURE_COLORS.swipes },
    { name: 'Checklisten', value: featureStats.checklists, color: FEATURE_COLORS.checklists },
    { name: 'Termine', value: featureStats.appointments, color: FEATURE_COLORS.appointments },
    { name: 'AI-Fragen', value: featureStats.ai, color: FEATURE_COLORS.ai },
    { name: 'Partner', value: featureStats.partners, color: FEATURE_COLORS.partners },
  ] : [];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Feature-Nutzung</h1>
        <p className="text-slate-600">Welche Features werden wie oft genutzt?</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-500">Lädt Statistiken...</div>
        </div>
      ) : (
        <>
          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-4 h-4" style={{ color: FEATURE_COLORS.swipes }} />
                <span className="text-xs text-slate-500">Namen-Swipes</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {featureStats?.swipes.toLocaleString('de-DE')}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {swipeAnalytics?.likeRate}% Like-Rate
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <ListChecks className="w-4 h-4" style={{ color: FEATURE_COLORS.checklists }} />
                <span className="text-xs text-slate-500">Checklisten</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {featureStats?.checklists.toLocaleString('de-DE')}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {checklistStats?.completionRate}% Abschlussrate
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4" style={{ color: FEATURE_COLORS.appointments }} />
                <span className="text-xs text-slate-500">Termine</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {featureStats?.appointments.toLocaleString('de-DE')}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4" style={{ color: FEATURE_COLORS.ai }} />
                <span className="text-xs text-slate-500">AI-Fragen</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {featureStats?.ai.toLocaleString('de-DE')}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Baby className="w-4 h-4" style={{ color: FEATURE_COLORS.partners }} />
                <span className="text-xs text-slate-500">Partner</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {featureStats?.partners.toLocaleString('de-DE')}
              </div>
            </div>
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Feature Distribution */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Feature-Verteilung</h2>
              {pieData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => value.toLocaleString('de-DE')} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                  Keine Daten vorhanden
                </div>
              )}
            </div>

            {/* Weekly Trends */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-teal-500" />
                <h2 className="text-lg font-semibold text-slate-900">Wöchentliche Nutzung</h2>
              </div>
              {weeklyTrends && weeklyTrends.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weeklyTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="swipes" fill={FEATURE_COLORS.swipes} name="Swipes" stackId="a" />
                      <Bar dataKey="appointments" fill={FEATURE_COLORS.appointments} name="Termine" stackId="a" />
                      <Bar dataKey="checklists" fill={FEATURE_COLORS.checklists} name="Checklisten" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                  Keine Daten vorhanden
                </div>
              )}
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Swipe Analytics */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-5 h-5" style={{ color: FEATURE_COLORS.swipes }} />
                <h2 className="text-lg font-semibold text-slate-900">Namen-Swipe Analyse</h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-emerald-600">
                    {swipeAnalytics?.likes.toLocaleString('de-DE')}
                  </div>
                  <div className="text-sm text-slate-600">Likes</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-500">
                    {swipeAnalytics?.dislikes.toLocaleString('de-DE')}
                  </div>
                  <div className="text-sm text-slate-600">Dislikes</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-amber-500">
                    {swipeAnalytics?.favorites.toLocaleString('de-DE')}
                  </div>
                  <div className="text-sm text-slate-600">Favoriten</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-500">
                    {swipeAnalytics?.likeRate}%
                  </div>
                  <div className="text-sm text-slate-600">Like-Rate</div>
                </div>
              </div>
            </div>

            {/* Appointment Types */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5" style={{ color: FEATURE_COLORS.appointments }} />
                <h2 className="text-lg font-semibold text-slate-900">Terminarten</h2>
              </div>
              {appointmentTypes && appointmentTypes.length > 0 ? (
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={appointmentTypes}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="label" width={80} />
                      <Tooltip formatter={(value: number) => value.toLocaleString('de-DE')} />
                      <Bar
                        dataKey="count"
                        radius={[0, 4, 4, 0]}
                      >
                        {appointmentTypes.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-slate-500">
                  Keine Termine vorhanden
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
